import React, { useEffect, useRef, useState } from 'react';
import type jsPDF from 'jspdf';
import { ProjectData, RabEntry } from '../../types';
import { generateUnterlagePdf } from '../../services/pdf/generatorUnterlage';
import { Button } from '../Button';
import { AlertTriangle, Download, FileText, Loader2, RefreshCw } from 'lucide-react';
import { istNativeApp } from '../../services/nativePlatform';
import { NativePdfSeiten } from '../NativePdfSeiten';

interface Props {
  projectData: ProjectData;
  entries: RabEntry[];
  orientation: 'portrait' | 'landscape';
  showRegulations: boolean;
  coverImageSize: 'small' | 'medium' | 'large';
  /**
   * Export läuft über den Aufrufer (Abrechnungsauswahl, Buchung, Journal-
   * Vormerkung — derselbe Weg wie „PDF exportieren" im Kopf). Ohne Callback
   * speichert der Knopf das gehaltene Dokument direkt; das ist nur für
   * Aufrufer ohne Abrechnung gedacht.
   */
  onDownload?: () => void;
  downloadDisabled?: boolean;
}

/** Wartezeit nach der letzten Prop-Änderung, bevor neu erzeugt wird. */
const DEBOUNCE_MS = 600;

/**
 * Vorschau des Moduls "Unterlage für spätere Arbeiten": zeigt das ECHTE PDF aus
 * `generateUnterlagePdf`, keine HTML-Nachbildung. Was hier steht, kommt auch aus
 * dem Download — Abweichungen zwischen Vorschau und Datei sind damit ausgeschlossen.
 */
export const UfsaPdfPreview: React.FC<Props> = ({ projectData, entries, orientation, showRegulations, coverImageSize, onDownload, downloadDisabled = false }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Das erzeugte Dokument wird gehalten und beim Download wiederverwendet —
  // keine teure Zweit-Generierung und garantiert dieselbe Datei wie im Rahmen.
  const docRef = useRef<jsPDF | null>(null);
  // Aktive Blob-URL separat halten: Freigabe NUR beim Ersetzen und beim Unmount,
  // sonst stirbt die gerade im iframe laufende URL (leere Vorschau).
  const urlRef = useRef<string | null>(null);
  // Laufnummer der Generierung. Eine langsamere ÄLTERE darf ein neueres
  // Ergebnis niemals überschreiben.
  const genRef = useRef(0);
  // Android-App: kein PDF-Betrachter in der WebView — Seiten per pdf.js
  // (NativePdfSeiten). Deren Ausgabe-Knopf nimmt denselben Weg wie
  // „PDF exportieren" (handleDownload): Abrechnung/Buchung über onDownload,
  // sonst doc.save → Abfang-Schicht (nativeShims) → Teilen-Dialog.
  // Browser: unverändert.
  const nativ = istNativeApp();

  useEffect(() => {
    const myGen = ++genRef.current;
    setBusy(true);

    const build = async () => {
      try {
        const doc = await generateUnterlagePdf(projectData, entries, {
          output: 'doc',
          orientation,
          showRegulations,
          coverImageSize,
          showTOC: false
        });
        if (myGen !== genRef.current) return;
        docRef.current = doc;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        const url = URL.createObjectURL(doc.output('blob'));
        urlRef.current = url;
        setPdfUrl(url);
        setPageCount(doc.getNumberOfPages());
        setError(null);
      } catch (e) {
        if (myGen !== genRef.current) return;
        console.error('UfsA PDF-Vorschau', e);
        setError(e instanceof Error ? e.message : 'Unbekannter Fehler bei der PDF-Erzeugung');
      } finally {
        if (myGen === genRef.current) setBusy(false);
      }
    };

    const timer = window.setTimeout(build, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [projectData, entries, orientation, showRegulations, coverImageSize, reloadToken]);

  // Beim Unmount: letzte Blob-URL freigeben und laufende Generierung entwerten.
  useEffect(() => () => {
    genRef.current++;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const handleDownload = () => {
    if (onDownload) { onDownload(); return; }
    const doc = docRef.current;
    if (!doc) return;
    // Dateiname exakt wie im Generator.
    doc.save(`Unterlage_${(projectData.projectName || 'Projekt').replace(/[^a-z0-9äöüß]/gi, '_')}.pdf`);
  };

  const firstRun = !pdfUrl && !error;

  return (
    <div className="w-full flex flex-col">
      <div className="flex items-center justify-between gap-3 px-3 py-2 bg-white border border-slate-200 rounded-t-xl shadow-xs">
        <div className="flex items-center gap-3 text-xs text-slate-600 min-w-0">
          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
            <FileText className="w-4 h-4 text-slate-400" />
            {pageCount > 0 ? `${pageCount} ${pageCount === 1 ? 'Seite' : 'Seiten'}` : '– Seiten'}
          </span>
          <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 whitespace-nowrap">
            {orientation === 'landscape' ? 'Querformat' : 'Hochformat'}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setReloadToken(t => t + 1)}
            icon={<RefreshCw className="w-3.5 h-3.5" />}
          >
            Neu erzeugen
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleDownload}
            disabled={(!onDownload && !pdfUrl) || downloadDisabled}
            icon={<Download className="w-3.5 h-3.5" />}
          >
            {onDownload ? 'PDF exportieren' : 'Herunterladen'}
          </Button>
        </div>
      </div>

      <div data-glass-papier className="relative w-full h-[calc(100vh-11rem)] min-h-[600px] bg-slate-100 border border-t-0 border-slate-200 rounded-b-xl overflow-hidden">
        {pdfUrl && nativ && (
          <NativePdfSeiten
            key={pdfUrl}
            pdfUrl={pdfUrl}
            onTeilen={handleDownload}
            teilenLaeuft={downloadDisabled}
            teilenText={onDownload ? 'PDF exportieren' : 'Herunterladen'}
          />
        )}
        {pdfUrl && !nativ && (
          <iframe title="PDF-Vorschau" src={pdfUrl} className="w-full h-full border-0" />
        )}

        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-slate-100">
            <AlertTriangle className="w-10 h-10 mb-3 text-amber-500" />
            <p className="font-bold text-slate-700 mb-1">Vorschau konnte nicht erstellt werden</p>
            <p className="text-xs text-slate-500 break-words max-w-md mb-4">{error}</p>
            <Button variant="secondary" size="sm" onClick={() => setReloadToken(t => t + 1)}>
              Erneut versuchen
            </Button>
          </div>
        )}

        {busy && !error && (
          firstRun ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
              <Loader2 className="w-9 h-9 animate-spin mb-3 text-blue-600" />
              <p className="font-bold text-sm">Vorschau wird erzeugt …</p>
              <p className="text-xs text-slate-400">Das echte PDF wird gerendert</p>
            </div>
          ) : (
            // Nicht blockierend: das bisherige PDF bleibt sichtbar und lesbar.
            <div className="absolute top-3 right-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/85 text-white text-[11px] font-semibold shadow-lg pointer-events-none">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Vorschau wird erzeugt …
            </div>
          )
        )}
      </div>
    </div>
  );
};
